// src/pages/StaffPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { dealershipRoleLabel } from "../utils/roleLabels";
import "./PlatformUsers.css";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function normalizeRoleValue(v) {
  const r = safeString(v).trim().toLowerCase();
  if (r === "tech") return "technician";
  return r;
}

function roleLabel(role) {
  const r = normalizeRoleValue(role);
  const map = {
    manager: "Service Manager",
    advisor: "Service Advisor",
    technician: "Technician",
    dispatcher: "Dispatcher",
    foreman: "Shop Foreman",
    parts: "Parts Manager",
    staff: "Parts Advisor",
    admin: dealershipRoleLabel("admin"),
  };
  return map[r] || r || "—";
}

function normalizeStatus(row) {
  const explicit = safeString(row?.status).trim().toUpperCase();

  if (explicit === "ACCOUNT_CLOSED") return "ACCOUNT_CLOSED";
  if (explicit === "TERMINATED") return "ACCOUNT_CLOSED";
  if (explicit === "CLOSED") return "ACCOUNT_CLOSED";
  if (explicit === "SUSPENDED") return "SUSPENDED";
  if (explicit === "ACTIVE") return "ACTIVE";

  return row?.isActive === false ? "SUSPENDED" : "ACTIVE";
}

function statusTone(status) {
  if (status === "ACCOUNT_CLOSED") return "muted";
  if (status === "SUSPENDED") return "danger";
  return "success";
}

function statusLabel(status) {
  if (status === "ACCOUNT_CLOSED") return "Account Closed";
  if (status === "SUSPENDED") return "Suspended";
  return "Active";
}

export function canUseStaffPinReveal(auth) {
  const role = normalizeRoleValue(
    auth?.storeRole || auth?.membership?.role || auth?.user?.role
  );
  return (
    role === "manager" ||
    role === "admin" ||
    role === "parts" ||
    role === "foreman"
  );
}

function Pill({ children, tone = "default" }) {
  return (
    <span className={`ftu-pill ${tone !== "default" ? `ftu-pill--${tone}` : ""}`}>
      {children}
    </span>
  );
}

function splitName(name) {
  const parts = safeString(name).trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function toStaffRow(e) {
  const name = safeString(e?.appUserName).trim();
  const email = safeString(e?.appUserEmail).trim().toLowerCase();
  const split = splitName(name);

  return {
    id: safeString(e?.id),
    appUserId: safeString(e?.appUserId),
    businessUnitId: safeString(e?.businessUnitId),
    appUserName: name || email || "—",
    appUserEmail: email,
    role: normalizeRoleValue(e?.role),
    isActive: e?.isActive !== false,
    status: safeString(e?.status).trim().toUpperCase(),
    storeName: safeString(e?.storeName),
    storeCode: safeString(e?.storeCode),
    firstName: split.firstName,
    lastName: split.lastName,
  };
}

export default function StaffPage() {
  const auth = useAuth();
  const { apiGet, apiPatch, apiPost } = auth;
  const canRevealPins = canUseStaffPinReveal(auth);
  const authUserId = safeString(auth?.user?.id || auth?.user?.userId);
  const authSessionKey = safeString(auth?.token || authUserId);
  const selectedStoreKey = safeString(auth?.effectiveStoreId || auth?.activeStoreId);

  const [staff, setStaff] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formFirstName, setFormFirstName] = useState("");
  const [formLastName, setFormLastName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formRole, setFormRole] = useState("technician");
  const [formStatus, setFormStatus] = useState("ACTIVE");

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("technician");
  const [inviteSaving, setInviteSaving] = useState(false);

  const [revealedPin, setRevealedPin] = useState("");
  const [pinRevealStatus, setPinRevealStatus] = useState("hidden");
  const [pinRevealMessage, setPinRevealMessage] = useState("");

  function clearPinReveal() {
    setRevealedPin("");
    setPinRevealStatus("hidden");
    setPinRevealMessage("");
  }

  async function load() {
    setLoading(true);
    setErr("");

    try {
      const data = await apiGet("/api/store/staff");
      const rows = Array.isArray(data?.staff) ? data.staff : [];
      const mapped = rows.map(toStaffRow);

      setStaff(mapped);

      if (selectedId && !mapped.some((row) => row.id === selectedId)) {
        setSelectedId("");
        setEditing(false);
      }
    } catch (e) {
      console.error(e);
      setErr(safeString(e?.message || e) || "Failed to load staff.");
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = safeString(query).trim().toLowerCase();
    if (!q) return staff;

    return staff.filter((s) =>
      `${s.appUserName} ${s.firstName} ${s.lastName} ${s.appUserEmail} ${s.role} ${roleLabel(
        s.role
      )} ${s.storeName} ${s.storeCode} ${statusLabel(normalizeStatus(s))}`
        .toLowerCase()
        .includes(q)
    );
  }, [staff, query]);

  const selected = useMemo(
    () => staff.find((s) => s.id === selectedId) || null,
    [staff, selectedId]
  );

  useEffect(() => {
    setRevealedPin("");
    setPinRevealStatus("hidden");
    setPinRevealMessage("");
  }, [selectedId, selectedStoreKey, authSessionKey]);

  useEffect(() => {
    if (!selected) {
      setEditing(false);
      setFormFirstName("");
      setFormLastName("");
      setFormEmail("");
      setFormRole("technician");
      setFormStatus("ACTIVE");
      return;
    }

    setEditing(false);
    setFormFirstName(selected.firstName || "");
    setFormLastName(selected.lastName || "");
    setFormEmail(selected.appUserEmail || "");
    setFormRole(normalizeRoleValue(selected.role || "technician"));
    setFormStatus(normalizeStatus(selected));
  }, [selected]);

  function clearMessages() {
    setErr("");
    setOkMsg("");
  }

  function openInvite() {
    clearMessages();
    setInviteFirstName("");
    setInviteLastName("");
    setInviteEmail("");
    setInviteRole("technician");
    setInviteOpen(true);
  }

  async function sendInvite() {
    clearMessages();
    setInviteSaving(true);

    try {
      const payload = {
        firstName: safeString(inviteFirstName).trim(),
        lastName: safeString(inviteLastName).trim(),
        email: safeString(inviteEmail).trim(),
        role: normalizeRoleValue(inviteRole),
      };

      if (!payload.firstName) throw new Error("First name is required.");
      if (!payload.lastName) throw new Error("Last name is required.");
      if (!payload.email) throw new Error("Email is required.");
      if (!payload.role) throw new Error("Role is required.");

      const data = await apiPost("/api/store-admin/invite", payload);

      if (data?.sent === false) {
        throw new Error(
          safeString(data?.emailError) || "Invite was created but email did not send."
        );
      }

      setInviteOpen(false);
      setOkMsg("Success: Invite sent.");
      await load();
    } catch (e) {
      setErr(safeString(e?.message || e) || "Failed to send invite.");
    } finally {
      setInviteSaving(false);
    }
  }

  async function saveEdit() {
    if (!selected?.id) return;

    clearMessages();

    const currentStatus = normalizeStatus(selected);
    const nextStatus = safeString(formStatus).trim().toUpperCase();
    const isAccountClosed = nextStatus === "ACCOUNT_CLOSED";

    if (nextStatus !== currentStatus) {
      let message = "";

      if (isAccountClosed) {
        message =
          "Close this account?\n\nThis will disable access and preserve the staff/history record for audit purposes.";
      } else if (nextStatus === "SUSPENDED") {
        message =
          "Suspend this staff member?\n\nThey will remain in Flowtrak, but their store access will be disabled until reactivated.";
      } else if (nextStatus === "ACTIVE") {
        message = "Reactivate this staff member?";
      }

      if (message) {
        const ok = window.confirm(message);
        if (!ok) return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        firstName: safeString(formFirstName).trim(),
        lastName: safeString(formLastName).trim(),
        email: safeString(formEmail).trim().toLowerCase(),
        role: normalizeRoleValue(formRole),
        status: nextStatus,
      };

      if (!payload.firstName) throw new Error("First name is required.");
      if (!payload.lastName) throw new Error("Last name is required.");
      if (!payload.email || !payload.email.includes("@")) {
        throw new Error("Valid email is required.");
      }
      if (!payload.role) throw new Error("Role is required.");
      if (!payload.status) throw new Error("Status is required.");

      const data = await apiPatch(
        `/api/store/staff/${encodeURIComponent(selected.id)}/full`,
        payload
      );

      setEditing(false);

      if (data?.closed) {
        setOkMsg("Success: Account closed and history preserved.");
      } else {
        setOkMsg("Success: Staff member updated.");
      }

      await load();
    } catch (e) {
      setErr(safeString(e?.message || e) || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  async function revealStaffPin() {
    if (!selected?.id || !canRevealPins) return;

    setRevealedPin("");
    setPinRevealStatus("loading");
    setPinRevealMessage("");

    try {
      const data = await apiGet(
        `/api/store/staff/${encodeURIComponent(selected.id)}/pin`
      );
      const pin = safeString(data?.pinDisplay).trim();

      if (data?.recoverable === true && pin) {
        setRevealedPin(pin);
        setPinRevealStatus("shown");
        setPinRevealMessage("");
        return;
      }

      setPinRevealStatus("unrecoverable");
      setPinRevealMessage("PIN cannot be retrieved");
    } catch {
      setRevealedPin("");
      setPinRevealStatus("error");
      setPinRevealMessage("PIN unavailable");
    }
  }

  function hideStaffPin() {
    clearPinReveal();
  }

  function cancelEdit() {
    if (!selected) {
      setEditing(false);
      return;
    }

    setEditing(false);
    setFormFirstName(selected.firstName || "");
    setFormLastName(selected.lastName || "");
    setFormEmail(selected.appUserEmail || "");
    setFormRole(normalizeRoleValue(selected.role || "technician"));
    setFormStatus(normalizeStatus(selected));
  }

  const selectedStatus = selected ? normalizeStatus(selected) : "ACTIVE";

  return (
    <div className="ft-page ftu-page">
      <div className="ftu-shell">
        <div className="ftu-pageTop">
          <div className="ftu-titleWrap">
            <h1 className="ft-title">Staff</h1>
            <p className="ft-subtitle">Manage your store team</p>
          </div>

          <div className="ftu-pageActions">
            <button className="ft-btn ft-btn--ghost" onClick={load} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button className="ft-btn ft-btn--primary" onClick={openInvite}>
              + Invite Staff
            </button>
          </div>
        </div>

        {err ? (
          <div className="ft-card ft-card--error" style={{ marginBottom: 12 }}>
            <div className="ft-errorText">{err}</div>
          </div>
        ) : null}

        {okMsg ? (
          <div
            className="ft-card"
            style={{
              marginBottom: 12,
              border: "1px solid rgba(34,197,94,0.25)",
              background: "rgba(34,197,94,0.10)",
              color: "#14532d",
            }}
          >
            <div style={{ fontWeight: 900 }}>{okMsg}</div>
          </div>
        ) : null}

        <div className="ftu-workspace">
          <div className="ftu-searchCard">
            <input
              className="ft-input ftu-searchInput"
              placeholder="Search staff..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="ftu-directoryLayout">
            <div className="ft-card ftu-directoryCard">
              <div className="ft-card__header">
                <div style={{ fontWeight: 800 }}>Directory</div>
                <div style={{ opacity: 0.7 }}>{loading ? "Loading…" : `${filtered.length} shown`}</div>
              </div>

              <div className="ft-card__body">
                {loading ? (
                  <div className="ft-muted">Loading staff...</div>
                ) : filtered.length === 0 ? (
                  <div className="ft-muted">No staff found.</div>
                ) : (
                  <div className="ftu-list">
                    {filtered.map((s) => {
                      const status = normalizeStatus(s);

                      return (
                        <button
                          key={s.id}
                          className={`ftu-row ${selectedId === s.id ? "is-active" : ""}`}
                          onClick={() => {
                            clearMessages();
                            setSelectedId(selectedId === s.id ? "" : s.id);
                          }}
                        >
                          <div className="ftu-rowMain">
                            <div className="ftu-rowIdentity">
                              <div className="ftu-name">{s.appUserName}</div>
                              <div className="ftu-email">{s.appUserEmail}</div>
                            </div>

                            <div className="ftu-rowPills">
                              <Pill>{roleLabel(s.role)}</Pill>
                              <Pill tone={statusTone(status)}>{statusLabel(status)}</Pill>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className={`ft-card ftu-profileCard ${editing ? "is-editing" : ""}`}>
              {selected ? (
                <>
                  <div className="ft-card__header">
                    <div>Profile</div>
                    <button
                      className="ft-btn ft-btn--ghost ftu-minBtn"
                      onClick={() => {
                        setSelectedId("");
                        setEditing(false);
                        clearMessages();
                      }}
                      title="Minimize"
                      aria-label="Minimize"
                    >
                      −
                    </button>
                  </div>

                  <div className="ft-card__body">
                    <div className="ftu-profileHero">
                      <div className="ftu-profileHeroText">
                        <div className="ftu-profileName">{selected.appUserName}</div>
                        <div className="ftu-profileEmail">{selected.appUserEmail}</div>
                      </div>

                      <div className="ftu-profilePills">
                        <Pill>{roleLabel(selected.role)}</Pill>
                        <Pill tone={statusTone(selectedStatus)}>{statusLabel(selectedStatus)}</Pill>
                      </div>

                      {editing ? (
                        <div className="ftu-editBanner">
                          Editing profile details and store access
                        </div>
                      ) : null}
                    </div>

                    <div className="ftu-section">
                      <div className="ftu-sectionTitle">Identity</div>

                      <div className="ftu-formGrid">
                        <div>
                          <div className="ft-label">First Name</div>
                          {editing ? (
                            <input
                              className="ft-input"
                              value={formFirstName}
                              onChange={(e) => setFormFirstName(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftu-readonly">{selected.firstName || "—"}</div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Last Name</div>
                          {editing ? (
                            <input
                              className="ft-input"
                              value={formLastName}
                              onChange={(e) => setFormLastName(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftu-readonly">{selected.lastName || "—"}</div>
                          )}
                        </div>

                        <div className="ftu-span2">
                          <div className="ft-label">Email</div>
                          {editing ? (
                            <input
                              className="ft-input"
                              value={formEmail}
                              onChange={(e) => setFormEmail(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftu-readonly">{selected.appUserEmail}</div>
                          )}
                        </div>

                        <div className="ftu-span2">
                          <div className="ft-label">PIN</div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <div className="ftu-readonly">
                              {pinRevealStatus === "shown"
                                ? revealedPin
                                : pinRevealStatus === "unrecoverable" ||
                                  pinRevealStatus === "error"
                                ? pinRevealMessage
                                : canRevealPins
                                ? "Hidden"
                                : "—"}
                            </div>
                            {canRevealPins ? (
                              pinRevealStatus === "shown" ? (
                                <button
                                  type="button"
                                  className="ft-btn ft-btn--ghost"
                                  onClick={hideStaffPin}
                                >
                                  Hide PIN
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ft-btn ft-btn--ghost"
                                  onClick={revealStaffPin}
                                  disabled={pinRevealStatus === "loading"}
                                >
                                  {pinRevealStatus === "loading"
                                    ? "Revealing..."
                                    : "Reveal PIN"}
                                </button>
                              )
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="ftu-section">
                      <div className="ftu-sectionTitle">Employment</div>

                      <div className="ftu-formGrid">
                        <div>
                          <div className="ft-label">Role</div>
                          {editing ? (
                            <select
                              className="ft-select"
                              value={formRole}
                              onChange={(e) => setFormRole(e.target.value)}
                              disabled={saving}
                            >
                              <option value="manager">Service Manager</option>
                              <option value="dispatcher">Dispatcher</option>
                              <option value="advisor">Service Advisor</option>
                              <option value="technician">Technician</option>
                              <option value="foreman">Shop Foreman</option>
                              <option value="parts">Parts Manager</option>
                              <option value="staff">Parts Advisor</option>
                              <option value="admin">Store Administrator</option>
                            </select>
                          ) : (
                            <div className="ftu-readonly">{roleLabel(selected.role)}</div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Status</div>
                          {editing ? (
                            <select
                              className="ft-select"
                              value={formStatus}
                              onChange={(e) => setFormStatus(e.target.value)}
                              disabled={saving}
                            >
                              <option value="ACTIVE">Active</option>
                              <option value="SUSPENDED">Suspended</option>
                              <option value="ACCOUNT_CLOSED">Account Closed</option>
                            </select>
                          ) : (
                            <div className="ftu-readonly">{statusLabel(selectedStatus)}</div>
                          )}
                        </div>
                      </div>

                      {selected.storeName || selected.storeCode ? (
                        <div className="ftu-hint">
                          {selected.storeName || "Business Unit"}
                          {selected.storeCode ? ` (${selected.storeCode})` : ""}
                        </div>
                      ) : null}
                    </div>

                    <div className="ftu-footerBar">
                      {!editing ? (
                        <button
                          className="ft-btn ft-btn--primary"
                          onClick={() => {
                            clearMessages();
                            setEditing(true);
                          }}
                        >
                          Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="ft-btn ft-btn--ghost"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                          <button
                            className="ft-btn ft-btn--primary"
                            onClick={saveEdit}
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Save Changes"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="ftu-emptyState">
                  <div className="ftu-emptyTitle">Select a staff member</div>
                  <div className="ftu-emptyText">View details and manage access</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {inviteOpen ? (
          <div className="ft-modalOverlay" role="dialog" aria-modal="true">
            <div className="ft-modal">
              <div className="ft-modalHeader">
                <div>
                  <div className="ft-cardTitle">Invite Staff</div>
                  <div className="ft-muted" style={{ padding: 0, fontSize: 12 }}>
                    Send a welcome email to a new team member
                  </div>
                </div>

                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => !inviteSaving && setInviteOpen(false)}
                >
                  Close
                </button>
              </div>

              <div className="ft-modalBody">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <div className="ft-label">First Name</div>
                    <input
                      className="ft-input"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      disabled={inviteSaving}
                    />
                  </div>

                  <div>
                    <div className="ft-label">Last Name</div>
                    <input
                      className="ft-input"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      disabled={inviteSaving}
                    />
                  </div>
                </div>

                <div>
                  <div className="ft-label">Email</div>
                  <input
                    className="ft-input"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="name@dealership.com"
                    disabled={inviteSaving}
                  />
                </div>

                <div>
                  <div className="ft-label">Role</div>
                  <select
                    className="ft-select"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    disabled={inviteSaving}
                  >
                    <option value="manager">Service Manager</option>
                    <option value="dispatcher">Dispatcher</option>
                    <option value="advisor">Service Advisor</option>
                    <option value="technician">Technician</option>
                    <option value="foreman">Shop Foreman</option>
                    <option value="parts">Parts Manager</option>
                    <option value="staff">Parts Advisor</option>
                  </select>
                </div>
              </div>

              <div className="ft-modalFooter">
                <button
                  type="button"
                  className="ft-btn ft-btn--ghost"
                  onClick={() => setInviteOpen(false)}
                  disabled={inviteSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ft-btn ft-btn--primary"
                  onClick={sendInvite}
                  disabled={inviteSaving}
                >
                  {inviteSaving ? "Sending…" : "Send Invite"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
